import { api, ensureApiAuthenticated } from '../services/api';

/** Orders children live at `/orders/<child>`. Tray detail keeps the CK `:id` step. */
export const ORDER_PULL_PATH = '/orders/order-pull';

export const NO_ELIGIBLE_ORDERS_REASON = 'No eligible orders are waiting to pull.';

export function orderPullTrayPath(trayId: string): string {
  return `${ORDER_PULL_PATH}/${trayId}`;
}

export interface OrderPullTraySummary {
  id: string;
  status: string;
  started_at: string;
  order_count: number;
}

export interface OrderPullTrayList {
  trays: OrderPullTraySummary[];
  can_start: boolean;
  start_blocked_reason: string | null;
}

export interface OrderPullStartResult {
  id: string | null;
  status: string | null;
  started_at: string | null;
  started_by: string | null;
  order_count: number;
  orders: unknown[];
}

export interface OrderPullWalkSlot {
  slot_number: number;
  qty: number;
  banners: string[];
}

export type OrderPullExceptionKind = 'missing' | 'damaged';

export interface OrderPullException {
  id: string;
  tray_id: string;
  order_id: string;
  line_id: string;
  order_item_id: string;
  kind: string;
  qty: number;
  note: string | null;
  card_name: string;
  slot_number: number | null;
  created_at: string;
}

/** One snapshot line a Missing or Damaged action can flag. */
export interface OrderPullExceptionTarget {
  line_id: string;
  slot_number: number | null;
  qty: number;
  open_qty: number;
  exceptions: OrderPullException[];
}

export function shortPickKindLabel(kind: string): string {
  if (kind === 'missing') return 'Missing';
  if (kind === 'damaged') return 'Damaged';
  return kind;
}

export function shortPickTargets(line: OrderPullWalkLine): OrderPullExceptionTarget[] {
  if (line.exception_lines) {
    return line.exception_lines;
  }
  return line.line_ids.map((lineId, index) => {
    const slot = line.slots.length === line.line_ids.length
      ? line.slots[index]
      : line.slots.length === 1
        ? line.slots[0]
        : undefined;
    return {
      line_id: lineId,
      slot_number: slot?.slot_number ?? null,
      qty: line.qty,
      open_qty: line.qty,
      exceptions: [],
    };
  });
}

export interface OrderPullHighlights {
  foil: boolean;
  quantity: boolean;
  condition: boolean;
  price: boolean;
  language: boolean;
  frames: boolean;
}

export interface OrderPullWalkLine {
  product_id: string | null;
  card_name: string;
  set_name: string | null;
  collector_number: string | null;
  condition: string | null;
  printing: string;
  printing_stamp: string | null;
  is_foil: boolean;
  language: string;
  qty: number;
  slots: OrderPullWalkSlot[];
  slot_breakdown: string;
  section_price: string | null;
  price_unknown: boolean;
  section: string;
  location_bin: string | null;
  frame_badges: string[];
  line_ids: string[];
  picked: boolean;
  /** Same image URL View Inventory returns. Null when none is stored. */
  image_url?: string | null;
  /** Hover preview when a line still has a separate URL. Null when image_url is the only size. */
  image_preview_url?: string | null;
  scryfall_id?: string | null;
  reverse_scryfall_id?: string | null;
  reverse_image_url?: string | null;
  is_dual_faced?: boolean | null;
  banners: string[];
  highlights: OrderPullHighlights;
  exception_lines?: OrderPullExceptionTarget[];
}

export interface OrderPullSlotLine {
  order_item_id: string;
  card_name: string;
  snapshot_qty: number;
  current_qty: number | null;
  in_snapshot: boolean;
  banners: string[];
  line_id?: string | null;
  exceptions?: OrderPullException[];
}

export interface OrderPullSlot {
  slot_number: number;
  order_id: string;
  external_id: string | null;
  buyer_name: string | null;
  fulfillment_status: string | null;
  pullable: boolean;
  verified: boolean;
  banners: string[];
  lines: OrderPullSlotLine[];
}

export interface OrderPullTrayDetail {
  id: string;
  status: string;
  started_at: string;
  started_by: string | null;
  order_count: number;
  /** Slots this tray marked picked. Zero on a pulled tray that predates the marker. */
  marked_picked_count: number;
  main: OrderPullWalkLine[];
  secure: OrderPullWalkLine[];
  slots: OrderPullSlot[];
}

export interface OrderPullCancelSkip {
  order_id: string;
  current_status: string | null;
  reason: string;
}

export interface OrderPullCancelResult {
  tray_id: string;
  from_status: string;
  reverted: string[];
  skipped: OrderPullCancelSkip[];
}

export async function listOpenOrderPullTrays(): Promise<OrderPullTrayList> {
  await ensureApiAuthenticated();
  const response = await api.get<OrderPullTrayList>('/order-pull/trays');
  return response.data;
}

export async function startOrderPullTray(): Promise<OrderPullStartResult> {
  await ensureApiAuthenticated();
  const response = await api.post<OrderPullStartResult>('/order-pull/trays');
  return response.data;
}

export async function fetchOrderPullTray(trayId: string): Promise<OrderPullTrayDetail> {
  await ensureApiAuthenticated();
  const response = await api.get<OrderPullTrayDetail>(`/order-pull/trays/${trayId}`);
  return response.data;
}

export async function recordOrderPullException(
  trayId: string,
  lineId: string,
  kind: OrderPullExceptionKind,
  qty: number,
  note: string | null,
): Promise<OrderPullTrayDetail> {
  await ensureApiAuthenticated();
  const response = await api.post<OrderPullTrayDetail>(`/order-pull/trays/${trayId}/exceptions`, {
    line_id: lineId,
    kind,
    qty,
    note,
  });
  return response.data;
}

export async function setOrderPullLinesPicked(
  trayId: string,
  lineIds: string[],
  picked: boolean,
): Promise<OrderPullTrayDetail> {
  await ensureApiAuthenticated();
  const response = await api.patch<OrderPullTrayDetail>(`/order-pull/trays/${trayId}/picked`, {
    line_ids: lineIds,
    picked,
  });
  return response.data;
}

export async function setOrderPullSlotsVerified(
  trayId: string,
  slotNumbers: number[],
  verified: boolean,
): Promise<OrderPullTrayDetail> {
  await ensureApiAuthenticated();
  const response = await api.patch<OrderPullTrayDetail>(`/order-pull/trays/${trayId}/verified`, {
    slot_numbers: slotNumbers,
    verified,
  });
  return response.data;
}

export async function completeOrderPullTray(trayId: string): Promise<OrderPullTrayDetail> {
  await ensureApiAuthenticated();
  const response = await api.post<OrderPullTrayDetail>(`/order-pull/trays/${trayId}/complete`);
  return response.data;
}

export async function cancelOrderPullTray(trayId: string): Promise<OrderPullCancelResult> {
  await ensureApiAuthenticated();
  const response = await api.post<OrderPullCancelResult>(`/order-pull/trays/${trayId}/cancel`);
  return response.data;
}
